import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/compat/router';
import { useMutation } from '@apollo/client';

import useStores from 'shared/hooks/use-stores';
import {
  RemoveCheckoutItemDocument,
  UpdateCheckoutDocument,
  UpdateCheckoutItemQuantityDocument,
} from 'src/gql/graphql';
import type {
  RemoveCheckoutItemMutationVariables,
  UpdateCheckoutItemQuantityMutationVariables,
  UpdateCheckoutMutationVariables,
} from 'src/gql/graphql';
import { checkoutViewerSecretContext, getCheckoutViewerSecret } from '../checkout-viewer-secret';

type UpdateCheckoutInput = Omit<UpdateCheckoutMutationVariables, 'checkoutId' | 'dispensaryId'>;
type UpdateCheckoutItemQuantityInput = Omit<UpdateCheckoutItemQuantityMutationVariables, 'checkoutId' | 'dispensaryId'>;
type RemoveCheckoutItemInput = Omit<RemoveCheckoutItemMutationVariables, 'checkoutId' | 'dispensaryId'>;

type UseUpdateServerCheckoutReturn = {
  handleUpdateCheckout: (params: UpdateCheckoutInput) => Promise<void>;
  handleUpdateQuantity: (params: UpdateCheckoutItemQuantityInput) => Promise<void>;
  handleRemoveItem: (params: RemoveCheckoutItemInput) => Promise<void>;
};

export function useUpdateServerCheckout(): UseUpdateServerCheckoutReturn {
  const router = useRouter();
  const { apolloClient, UI } = useStores();
  const { isPlus } = UI;

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const checkoutIdParam = typeof router?.query.checkoutId !== 'string' ? '' : router?.query.checkoutId;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- router can be null
  const dispensaryIdParam = typeof router?.query.dispensaryId !== 'string' ? '' : router?.query.dispensaryId;

  const skipUpdateCall = !isPlus || !checkoutIdParam || !dispensaryIdParam;
  const viewerContext = useCallback(() => checkoutViewerSecretContext(getCheckoutViewerSecret(checkoutIdParam)), [
    checkoutIdParam,
  ]);

  const baseMutationOptions = {
    client: apolloClient,
    refetchQueries: ['Checkout'],
  };

  const [updateCheckout] = useMutation(UpdateCheckoutDocument, baseMutationOptions);
  const [updateQuantity] = useMutation(UpdateCheckoutItemQuantityDocument, baseMutationOptions);
  const [removeItem] = useMutation(RemoveCheckoutItemDocument, baseMutationOptions);

  const handleUpdateCheckout = useCallback(
    async (params: UpdateCheckoutInput): Promise<void> => {
      if (skipUpdateCall) {
        return;
      }

      try {
        await updateCheckout({
          context: viewerContext(),
          variables: {
            dispensaryId: dispensaryIdParam,
            checkoutId: checkoutIdParam,
            ...params,
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [checkoutIdParam, dispensaryIdParam, updateCheckout, skipUpdateCall, viewerContext]
  );

  const handleUpdateQuantity = useCallback(
    async (params: UpdateCheckoutItemQuantityInput): Promise<void> => {
      if (skipUpdateCall) {
        return;
      }

      try {
        await updateQuantity({
          context: viewerContext(),
          variables: {
            dispensaryId: dispensaryIdParam,
            checkoutId: checkoutIdParam,
            ...params,
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [checkoutIdParam, dispensaryIdParam, updateQuantity, skipUpdateCall, viewerContext]
  );

  const handleRemoveItem = useCallback(
    async (params: RemoveCheckoutItemInput): Promise<void> => {
      if (skipUpdateCall) {
        return;
      }

      try {
        await removeItem({
          context: viewerContext(),
          variables: {
            dispensaryId: dispensaryIdParam,
            checkoutId: checkoutIdParam,
            ...params,
          },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [checkoutIdParam, dispensaryIdParam, removeItem, skipUpdateCall, viewerContext]
  );

  return {
    handleUpdateCheckout,
    handleUpdateQuantity,
    handleRemoveItem,
  };
}

export function useSyncOrderTypeAndAddress(): void {
  const { Cart, UI } = useStores();
  const { orderType } = Cart;
  const { tempLocation } = UI;
  const { handleUpdateCheckout } = useUpdateServerCheckout();

  useEffect(() => {
    const defaultLocation = {
      city: '',
      state: '',
      street1: '',
      street2: '',
      zip: '',
    };

    const { city, ln1: street1, ln2: street2, state, zipcode: zip } = tempLocation ?? defaultLocation;

    const address =
      orderType === 'delivery'
        ? {
            city,
            state,
            street1,
            street2,
            zip,
          }
        : defaultLocation;

    void handleUpdateCheckout({
      orderType,
      address,
    });
  }, [handleUpdateCheckout, orderType, tempLocation]);
}
